import { cn } from "@/lib/utils";
import type { AvailableChartColorsKeys } from "@/lib/utils/chartUtils";
import { useCallback, useMemo } from "react";
import type { FeedVolumeGraphTooltipProps } from "./NewsFeedVolumeGraph.types";
import { BasicToolTipFooter } from "./NewsFeedVolumeGraphToolTipFooter";
import { NewsFeedVolumeGraphToolTipMessage } from "./NewsFeedVolumeGraphToolTipMessage";
import {
  getChartStackedByOptions,
  normalizeStackByToOldFormat,
} from "./feedVolumeGraphHelpers";

const NewsFeedVolumeGraphTooltip = ({
  active,
  payload,
  stackedBy,
  period,
}: FeedVolumeGraphTooltipProps) => {
  const selectedStackByOption = useMemo(() => {
    // Normalize stackedBy to old format for comparison (handles both enum and old format)
    const normalizedStackBy =
      normalizeStackByToOldFormat(stackedBy) ?? stackedBy;
    return getChartStackedByOptions().find(
      (option) => option.value === normalizedStackBy,
    );
  }, [stackedBy]);

  // Filter payload to only include valid items with data
  // Must be called before any conditional returns to follow Rules of Hooks
  const validPayload = useMemo(
    () =>
      payload && payload.length > 0
        ? payload.filter(
            ({ value, payload: payloadItem }) =>
              value !== undefined &&
              value !== null &&
              value > 0 &&
              payloadItem?.startOfPeriod &&
              payloadItem?.endOfPeriod &&
              typeof payloadItem.volume === "number",
          )
        : [],
    [payload],
  );

  // Must be called before any conditional returns to follow Rules of Hooks
  const renderTooltipContent = useCallback(
    (
      value: number,
      category: string,
      color: AvailableChartColorsKeys,
      payload: {
        startOfPeriod: string;
        endOfPeriod: string;
        volume: number;
      },
    ) => {
      // const clusterStats = getClusterStatsForPeriod?.(
      //   payload.startOfPeriod,
      //   payload.endOfPeriod,
      //   category,
      // );

      // Normalize stackedBy to old format for switch statement (handles both enum and old format)
      const normalizedStackedBy =
        normalizeStackByToOldFormat(stackedBy) ?? stackedBy;

      switch (normalizedStackedBy) {
        case "readership":
        case "domain_authority":
        case "social":
        case "impact_score":
        case "sentiment":
        case "prominence":
        case "content_category":
        case "tier":
          return (
            <>
              <NewsFeedVolumeGraphToolTipMessage
                stackedBy={stackedBy}
                category={category}
                value={value}
                color={color}
              />
              <BasicToolTipFooter
                totalArticles={value}
                startDate={payload.startOfPeriod}
                endDate={payload.endOfPeriod}
                period={period}
                selectedStackByOptionValue={selectedStackByOption?.value}
                volume={payload.volume}
                stackedBy={stackedBy}
              />
            </>
          );
        // case "narrative_clusters":
        //   return (
        //     <>
        //       <FeedVolumeGraphToolTipMessage
        //         stackedBy={stackedBy}
        //         category={category}
        //         value={value}
        //         color={color}
        //       />
        //       <ListToolTipFooter
        //         totalArticles={clusterStats?.totalArticles ?? 0}
        //         volume={payload.volume}
        //         stackedBy={stackedBy}
        //         sentiment={clusterStats?.netSentiment ?? "NEUTRAL"}
        //         socialEngagement={clusterStats?.socialEngagement ?? 0}
        //         readership={clusterStats?.readership ?? 0}
        //         grossImpact={clusterStats?.grossImpact ?? 0}
        //         netImpact={clusterStats?.netImpact ?? 0}
        //       />
        //     </>
        //   );
        case null:
          return (
            <NewsFeedVolumeGraphToolTipMessage
              stackedBy={stackedBy}
              category={category}
              value={value}
              color={color}
            />
          );
        default:
          return null;
      }
    },
    [stackedBy, selectedStackByOption, period],
  );

  // Don't render tooltip if not active, no payload, or no valid payload items
  if (
    !active ||
    !payload ||
    payload.length === 0 ||
    validPayload.length === 0
  ) {
    return null;
  }

  return (
    <div
      className={cn(
        "rounded-md border text-[10px] shadow-md",
        "border-gray-200 dark:border-gray-800",
        "bg-white dark:bg-gray-950",
        "min-w-48",
      )}
    >
      {/* Header */}
      <div className={"px-4 pt-2 flex items-center"}>
        <p
          className={
            "text-gray-600 dark:text-gray-50 text-[8px] uppercase font-medium tracking-wide mr-auto"
          }
        >
          {selectedStackByOption?.value
            ? selectedStackByOption.label
            : "Volume (All)"}
        </p>
        <span className="text-gray-700 text-sm">
          {selectedStackByOption?.icon}
        </span>
      </div>
      {/* Body */}
      <div className={"space-y-1 px-4 pt-1 pb-2"}>
        {validPayload.map(
          ({ value, category, color, payload: payloadItem }, index) => {
            return (
              <div
                key={`id-${index}-${value}-${category}`}
                className="flex flex-col gap-1 max-w-48"
              >
                {renderTooltipContent(
                  value,
                  category,
                  color as AvailableChartColorsKeys,
                  {
                    startOfPeriod: payloadItem.startOfPeriod,
                    endOfPeriod: payloadItem.endOfPeriod,
                    volume: payloadItem.volume,
                  },
                )}
              </div>
            );
          },
        )}
      </div>
    </div>
  );
};

NewsFeedVolumeGraphTooltip.displayName = "NewsFeedVolumeGraphTooltip";
export default NewsFeedVolumeGraphTooltip;
